import { useTranslation } from 'react-i18next';
import { FiArrowRight, FiInfo } from 'react-icons/fi';
import {
  DASH,
  fmtDelta,
  fmtIndex,
  levelStyle,
  sortByAwareness,
} from '../../Utils/awarenessDepartments';

/**
 * Riepilogo per reparto mostrato nella dashboard Awareness.
 *
 * Consuma `awareness_by_department`, già incluso nel payload della dashboard:
 * per il riepilogo non serve nessuna chiamata aggiuntiva e in particolare non
 * va mai richiesta la heatmap.
 */
export default function AwarenessDepartmentSummary({ data, onOpenDetail }) {
  const { t } = useTranslation();

  if (!data) return null;

  const levels = data.levels || {};
  const departments = sortByAwareness(data.departments || []);
  const companyAverage = data.company_average || null;
  const unassigned = data.unassigned || null;
  const smallSampleUsers = data.small_sample_users;

  if (!departments.length && !unassigned) return null;

  const Row = ({ entry, label, highlighted = false, outsideRanking = false }) => {
    const style = levelStyle(entry.level, levels);
    return (
      <div
        className={`flex items-center gap-3 px-3 py-2 rounded-lg ${
          highlighted ? 'bg-yellow-50 border border-yellow-300' : 'bg-gray-50'
        }`}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={`font-medium truncate ${highlighted ? 'text-gray-900' : 'text-gray-800'}`}>
              {label || entry.department_name || DASH}
            </span>
            {entry.small_sample && (
              <span
                className="text-gray-400 text-xs"
                title={t('awarenessDepartments.smallSampleHint', { count: smallSampleUsers })}
              >
                *
              </span>
            )}
            {outsideRanking && (
              <span className="text-[10px] text-gray-500 border border-gray-300 rounded px-1">
                {t('awarenessDepartments.outsideRanking')}
              </span>
            )}
          </div>
          <div className="text-xs text-gray-500">
            {t('awarenessDepartments.activeUsersCount', { count: entry.users_count ?? 0 })}
            {entry.vs_company_average !== undefined && entry.vs_company_average !== null && (
              <span className={Number(entry.vs_company_average) < 0 ? 'text-red-600 ml-2' : 'text-emerald-600 ml-2'}>
                {fmtDelta(entry.vs_company_average)} {t('awarenessDepartments.vsAverageShort')}
              </span>
            )}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-lg font-bold text-gray-900 leading-none">{fmtIndex(entry.awareness_index)}</div>
          <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${style.badge}`}>
            {t(`awarenessDepartments.levels.${entry.level}`, entry.level || DASH)}
          </span>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg p-4 border border-gray-200">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h3 className="text-base font-bold text-gray-900">{t('awarenessDepartments.summaryTitle')}</h3>
          <p className="text-xs text-gray-500">{t('awarenessDepartments.summarySubtitle')}</p>
        </div>
        {onOpenDetail && (
          <button
            type="button"
            onClick={onOpenDetail}
            className="flex items-center gap-1 text-sm font-semibold text-blue-600 hover:text-blue-800 shrink-0"
          >
            {t('awarenessDepartments.openDetail')} <FiArrowRight className="w-4 h-4" />
          </button>
        )}
      </div>

      <div className="space-y-2">
        {companyAverage && (
          <Row entry={companyAverage} label={t('awarenessDepartments.companyAverage')} highlighted />
        )}
        {departments.map((dept) => (
          <Row key={dept.department_id} entry={dept} />
        ))}
        {unassigned && (
          <Row entry={unassigned} label={t('awarenessDepartments.unassigned')} outsideRanking />
        )}
      </div>

      {data.counts_active_users_only && (
        <p className="mt-3 text-[11px] text-gray-500 flex items-start gap-1">
          <FiInfo className="w-3 h-3 mt-0.5 shrink-0" />
          {t('awarenessDepartments.activeOnlyShort')}
          {smallSampleUsers ? ` · ${t('awarenessDepartments.smallSampleFootnote', { count: smallSampleUsers })}` : ''}
        </p>
      )}
    </div>
  );
}
